import {
	MajorCardId,
	MinorCardNumber,
	Suit,
	MinorCardId,
	type MajorCardSpec,
	type MinorCardSpec
} from './types';

//
const courtHierarchy = [
	MinorCardNumber.PAGE,
	MinorCardNumber.KNIGHT,
	MinorCardNumber.QUEEN,
	MinorCardNumber.KING
];

const createMajorCard = ({ id }: { id: MajorCardId }): MajorCardSpec => {
	return {
		_tag: 'MajorCardSpec',
		id,
		cloudinaryPaths: {
			back: 'Major Arcana/MAJOR_ARCANA_BACK',
			front: `Major Arcana/${id}`
		}
	};
};

export const majorCards: Record<MajorCardId, MajorCardSpec> = {
	[MajorCardId.MAGICIAN]: createMajorCard({ id: MajorCardId.MAGICIAN }),
	[MajorCardId.HIEROPHANT]: createMajorCard({ id: MajorCardId.HIEROPHANT }),
	[MajorCardId.LOVERS]: createMajorCard({ id: MajorCardId.LOVERS }),
	[MajorCardId.CHARIOT]: createMajorCard({ id: MajorCardId.CHARIOT }),
	[MajorCardId.STRENGTH]: createMajorCard({ id: MajorCardId.STRENGTH }),
	[MajorCardId.HERMIT]: createMajorCard({ id: MajorCardId.HERMIT }),
	[MajorCardId.WHEEL_OF_FORTUNE]: createMajorCard({ id: MajorCardId.WHEEL_OF_FORTUNE }),
	[MajorCardId.JUSTICE]: createMajorCard({ id: MajorCardId.JUSTICE }),
	[MajorCardId.EMPEROR]: createMajorCard({ id: MajorCardId.EMPEROR }),
	[MajorCardId.EMPRESS]: createMajorCard({ id: MajorCardId.EMPRESS }),
	[MajorCardId.TEMPERANCE]: createMajorCard({ id: MajorCardId.TEMPERANCE }),
	[MajorCardId.DEVIL]: createMajorCard({ id: MajorCardId.DEVIL }),
	[MajorCardId.DEATH]: createMajorCard({ id: MajorCardId.DEATH }),
	[MajorCardId.JUDGEMENT]: createMajorCard({ id: MajorCardId.JUDGEMENT }),
	[MajorCardId.FOOL]: createMajorCard({ id: MajorCardId.FOOL }),
	[MajorCardId.STAR]: createMajorCard({ id: MajorCardId.STAR }),
	[MajorCardId.MOON]: createMajorCard({ id: MajorCardId.MOON }),
	[MajorCardId.TOWER]: createMajorCard({ id: MajorCardId.TOWER }),
	[MajorCardId.HANGED_MAN]: createMajorCard({ id: MajorCardId.HANGED_MAN }),
	[MajorCardId.SUN]: createMajorCard({ id: MajorCardId.SUN }),
	[MajorCardId.WORLD]: createMajorCard({ id: MajorCardId.WORLD }),
	[MajorCardId.HIGH_PRIESTESS]: createMajorCard({ id: MajorCardId.HIGH_PRIESTESS })
};

export const majorCardsList: MajorCardId[] = Object.values(MajorCardId);

const minorCardNumbers = [
	MinorCardNumber.ONE,
	MinorCardNumber.TWO,
	MinorCardNumber.THREE,
	MinorCardNumber.FOUR,
	MinorCardNumber.FIVE,
	MinorCardNumber.SIX,
	MinorCardNumber.SEVEN,
	MinorCardNumber.EIGHT,
	MinorCardNumber.NINE,
	MinorCardNumber.TEN,
	MinorCardNumber.PAGE,
	MinorCardNumber.KNIGHT,
	MinorCardNumber.QUEEN,
	MinorCardNumber.KING
];

const allSuits = [Suit.SWORD, Suit.CUPS, Suit.STAVES, Suit.COINS];

export const createMinorCardId = (suit: Suit, number: MinorCardNumber): MinorCardId =>
	`${suit}_${number}` as MinorCardId;

export const minorCardsList: MinorCardId[] = allSuits
	.map((suit) => minorCardNumbers.map((number) => createMinorCardSpec(suit, number).id))
	.flat();

function createMinorCardSpec(suit: Suit, number: MinorCardNumber): MinorCardSpec {
	const numberIndex = minorCardNumbers.indexOf(number);

	return {
		_tag: 'MinorCardSpec',
		suit,
		id: createMinorCardId(suit, number),
		hasNumber: (num) => num === number,
		nextNumber: minorCardNumbers[numberIndex + 1] || null,
		isCourtCard: courtHierarchy.includes(number),
		cloudinaryPaths: {
			front: `Minor Arcana/${suit}_${number}_FRONT`,
			back: `Minor Arcana/${suit}_${number}_BACK`
		}
	};
}

export function getMinorCardSpec(id: MinorCardId): MinorCardSpec {
	const [suit, number] = id.split('_') as [Suit, MinorCardNumber];
	return createMinorCardSpec(suit, number);
}

export function getMajorCardSpec(id: MajorCardId): MajorCardSpec {
	return majorCards[id];
}

export function checkIfNextCourtCard(
	thisId: MinorCardId,
	existingFinaleDeck: MinorCardId[]
): boolean {
	const thisCard = getMinorCardSpec(thisId);

	const lastSavedCardId = existingFinaleDeck.at(-1);

	if (!lastSavedCardId) {
		return thisCard.hasNumber(MinorCardNumber.PAGE);
	}

	const lastSavedCard = getMinorCardSpec(lastSavedCardId);
	const nextNumber = lastSavedCard.nextNumber;

	return !!nextNumber && thisCard.hasNumber(nextNumber);
}

// function extractMinorCardNumber(cardId: MinorCardId): MinorCardNumber {
// 	return cardId.split('_')[1] as MinorCardNumber;
// }

const majorCardsMap = new Map(majorCardsList.map((id) => [id, majorCards[id]]));

export function isMajorCardId(id: string): id is MajorCardId {
	return majorCardsMap.has(id as MajorCardId);
}
